import React from 'react';

interface AvatarProps {
  src?: string | null;
  alt?: string;
  name?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  onClick?: () => void;
  status?: 'online' | 'offline' | 'away' | 'busy' | 'none';
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  alt = 'User',
  name,
  size = 'md',
  className = '',
  onClick,
  status = 'none',
}) => {
  // Size classes
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
  };
  
  // Status color classes
  const statusClasses = {
    online: 'bg-green-500',
    offline: 'bg-gray-500',
    away: 'bg-yellow-500',
    busy: 'bg-red-500',
    none: 'hidden',
  };
  
  // Status size classes
  const statusSizeClasses = {
    xs: 'w-1.5 h-1.5',
    sm: 'w-2 h-2',
    md: 'w-2.5 h-2.5',
    lg: 'w-3 h-3',
    xl: 'w-4 h-4',
  };
  
  // Get initials from name
  const getInitials = () => {
    if (!name) return '?';
    
    const nameParts = name.split(' ');
    
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
    
    return (
      nameParts[0].charAt(0).toUpperCase() + 
      nameParts[nameParts.length - 1].charAt(0).toUpperCase()
    );
  };
  
  // Background color based on name (for consistent colors per user)
  const getBackgroundColor = () => {
    if (!name) return 'bg-primary-600';
    
    const colors = [
      'bg-primary-600',
      'bg-blue-600',
      'bg-green-600',
      'bg-yellow-600',
      'bg-red-600',
      'bg-purple-600',
      'bg-pink-600',
      'bg-indigo-600',
    ];
    
    const hash = name.split('').reduce((acc, char) => {
      return acc + char.charCodeAt(0);
    }, 0);
    
    return colors[hash % colors.length];
  };
  
  // Cursor class
  const cursorClass = onClick ? 'cursor-pointer' : '';
  
  return (
    <div className="relative inline-flex">
      {src ? (
        // Image avatar
        <img
          src={src}
          alt={alt}
          className={`
            rounded-full object-cover border-2 border-white/10
            ${sizeClasses[size]}
            ${cursorClass}
            ${className}
          `}
          onClick={onClick}
        />
      ) : (
        // Initials avatar
        <div
          className={`
            flex items-center justify-center rounded-full text-white
            ${getBackgroundColor()}
            ${sizeClasses[size]}
            ${cursorClass}
            ${className}
          `}
          onClick={onClick}
        >
          {getInitials()}
        </div>
      )}
      
      {/* Status indicator */}
      {status !== 'none' && (
        <span 
          className={`
            absolute bottom-0 right-0 rounded-full ring-2 ring-background-dark
            ${statusClasses[status]}
            ${statusSizeClasses[size]}
          `}
        ></span>
      )}
    </div>
  );
};

export default Avatar;
